import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import useEquipmentCatalog from '../../hooks/useEquipmentCatalog';
import CreatableCombobox from '../CreatableCombobox';
import { extractErrorMessage } from '../../utils/apiErrors';
import { RecordSiteFields } from './RecordSiteFields';
import { recordSite, recordSitePatch } from '../../utils/siteLocation';
import UnitSpecFields from './UnitSpecFields';
import useCsmCatalogue from '../../hooks/useCsmCatalogue';
import { EquipmentSizeField } from '../EquipmentSizeField';
import { equipmentCascadePatch } from '../../utils/equipmentSizes';
import { UnitAttachments } from './UnitAttachments';
import { CatalogLoadState } from '../CatalogLoadState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { equipment_type: 'pump', style: '', make: '', model: '', size: '', serial_number: '', unit_id: '', product_pumped: '', notes: '', lsd: '', location: '', site_details: null, series: '', driver_type: '', specifications: {}, driver_specifications: {}, accessories: [], attachment_ids: [] };

export default function UnitFormDialog({ open, onOpenChange, customer, unit, onSaved, onSubmitUnit, dark = false }) {
  const catalog = useEquipmentCatalog();
  const initialType = useRef('');
  initialType.current = catalog.types.find((type) => type.value === 'pump')?.value || catalog.types[0]?.value || '';
  const refreshCatalog = catalog.refresh;
  const csm = useCsmCatalogue();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const sites = customer?.sites || [];
  const isEdit = !!unit?.asset_id || !!unit?.manual_id || (!!onSubmitUnit && !!unit?.id);

  useEffect(() => {
    if (open) setForm(unit ? { ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, unit[k] ?? EMPTY[k]])), size: unit.size ?? unit.specifications?.SIZE ?? '' } : { ...EMPTY, equipment_type: initialType.current });
  }, [open, unit]);

  useEffect(() => { if (open) refreshCatalog(); }, [open, refreshCatalog]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (saving || uploading) return;
    if ((!onSubmitUnit || form.attachment_ids.length) && !form.unit_id.trim() && !form.serial_number.trim()) return toast.error('Enter an Asset ID or Serial Number');
    setSaving(true);
    try {
      if (onSubmitUnit) await onSubmitUnit({ ...form, asset_id: unit?.asset_id || '' });
      else if (unit?.asset_id) await axios.patch(`${API}/customers/${customer.id}/assets/${unit.asset_id}`, { ...form, asset_id: unit.asset_id, asset_revision: unit.asset_revision });
      else if (isEdit) await axios.patch(`${API}/customer-units/${unit.manual_id}`, form);
      else await axios.post(`${API}/customers/${customer.id}/units`, form);
      if (!onSubmitUnit) toast.success(isEdit ? 'Asset updated' : 'Asset added');
      if (!onSubmitUnit) await catalog.refresh();
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to save asset'));
    } finally {
      setSaving(false);
    }
  };

  const onStyle = (style) => {
    set({ ...equipmentCascadePatch(form, 'style', style), specifications: {}, series: '', accessories: (form.accessories || []).map((a) => ({ ...a, type: '', manufacturer: '' })) });
  };
  const onMake = (make) => {
    set({ ...equipmentCascadePatch(form, 'make', make), specifications: {}, series: '' });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!saving && !uploading) onOpenChange(next); }}>
      <DialogContent overlayClassName="z-[110]" className={`z-[120] w-[calc(100%-1rem)] grid-cols-[minmax(0,1fr)] max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-xl ${dark ? 'dark bg-slate-900 border-slate-700 text-slate-100' : ''}`} data-testid="unit-form-dialog" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle data-testid="unit-form-title">{isEdit ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
          <DialogDescription data-testid="unit-form-context">{customer?.name ? `Asset details — ${customer.name}` : 'Customer asset details'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="unit-form">
          <CatalogLoadState catalog={catalog} prefix="unit-catalog" />
          <RecordSiteFields value={recordSite(form, sites)} onChange={(site) => set(recordSitePatch(site))} customer={customer} testPrefix="unit-site" dark={dark} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Equipment Type</Label>
              <Select value={form.equipment_type || 'none'} disabled={catalog.loading || !!catalog.error} onValueChange={(v) => set({ ...equipmentCascadePatch(form, 'equipment_type', v === 'none' ? '' : v), specifications: {}, series: '' })}>
                <SelectTrigger data-testid="unit-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent className="z-[130]">
                  <SelectItem value="none" data-testid="unit-type-none">No type</SelectItem>
                  {form.equipment_type && !catalog.types.some((t) => t.value === form.equipment_type) && <SelectItem value={form.equipment_type} data-testid="unit-type-legacy">{catalog.labelForType(form.equipment_type)}</SelectItem>}
                  {catalog.types.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`unit-type-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Style</Label>
              <CreatableCombobox value={form.style} options={catalog.stylesFor(form.equipment_type)} onChange={onStyle} noun="style" placeholder="Select or type" testid="unit-style" />
            </div>
            <div className="space-y-1.5">
              <Label>Manufacturer</Label>
              <CreatableCombobox value={form.make} options={catalog.manufacturersFor(form.equipment_type, form.style)} onChange={onMake} noun="manufacturer" placeholder="Select or type" testid="unit-make" />
            </div>
            <div className="space-y-1.5">
              <Label>Model</Label>
              <CreatableCombobox value={form.model} options={catalog.modelsFor(form.equipment_type, form.style, form.make)} onChange={(model) => set(equipmentCascadePatch(form, 'model', model))} noun="model" placeholder="Select or type" testid="unit-model" />
            </div>
            <EquipmentSizeField item={form} catalog={catalog} onChange={(size) => set({ size })} testid="unit-size" />
            <div className="space-y-1.5">
              <Label>Asset ID</Label>
              <Input value={form.unit_id} onChange={(e) => set({ unit_id: e.target.value })} placeholder="e.g. P-100" data-testid="unit-id-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Serial Number</Label>
              <Input value={form.serial_number} onChange={(e) => set({ serial_number: e.target.value })} data-testid="unit-serial-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Product Pumped</Label>
            <CreatableCombobox value={form.product_pumped} options={csm.products} onChange={(product_pumped) => set({ product_pumped })} noun="product" placeholder="Select or type a product" testid="unit-product-input" />
          </div>
          <UnitSpecFields form={form} set={set} />
          <div className="space-y-1.5">
            <Label>Notes / Comments</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="unit-notes-input" />
            <UnitAttachments ids={form.attachment_ids} onChange={(attachment_ids) => set({ attachment_ids })} onBusyChange={setUploading} disabled={saving} maxFiles={unit?.asset_id ? 200 : 20} />
          </div>
          <Button type="submit" disabled={saving || uploading} className="w-full" data-testid="unit-form-submit">
            {saving ? 'Saving...' : uploading ? 'Uploading...' : isEdit ? 'Update Asset' : 'Add Asset'}
          </Button>
          <Button type="button" variant="outline" disabled={saving || uploading} className="w-full" onClick={() => onOpenChange(false)} data-testid="unit-form-cancel">Cancel</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
